import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, User, Save } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMemberForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [createdMember, setCreatedMember] = useState(null);
  const [formData, setFormData] = useState({
    school_id: '',
    full_name: '',
    ic_masked: '',
    dob: '',
    gender: 'Lelaki',
    phone: '',
    address: ''
  });

  useEffect(() => {
    fetchActiveSchools();
  }, []);

  const fetchActiveSchools = async () => {
    try {
      const response = await axios.get(`${API_URL}/schools?status_filter=Active`);
      setSchools(response.data);
    } catch (error) {
      console.error('Failed to fetch schools:', error);
      toast.error('Gagal memuatkan senarai perguruan');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.school_id) {
      toast.error('Sila pilih perguruan');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/members`, formData);
      setCreatedMember(response.data);
      toast.success('Ahli berjaya didaftarkan!');
    } catch (error) {
      console.error('Failed to create member:', error);
      toast.error(error.response?.data?.detail || 'Gagal mendaftarkan ahli');
    } finally {
      setLoading(false);
    }
  };

  if (createdMember) {
    const school = schools.find(s => s.id === createdMember.school_id);
    const qrUrl = `${process.env.REACT_APP_BACKEND_URL}/verify?token=${createdMember.qr_token}`;
    
    return (
      <AdminLayout>
        <div className="space-y-6 max-w-4xl">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/members')}
            className="border-[#E5E5E5]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Senarai
          </Button>

          <Alert className="bg-green-50 border-2 border-green-300">
            <AlertDescription>
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-green-800">Ahli Berjaya Didaftarkan!</h3>
                <div className="bg-white rounded-md p-4 border border-green-200">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-[#777]">Nama Ahli:</span>
                      <span className="font-semibold text-[#1A1A1A]">{createdMember.full_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#777]">Nombor Ahli:</span>
                      <span className="font-mono font-semibold text-[#D71920]">{createdMember.member_no}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#777]">Perguruan:</span>
                      <span className="font-semibold text-[#1A1A1A]">{school?.name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#777]">Status:</span>
                      <span className="px-2 py-1 rounded text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                        {createdMember.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <p className="text-sm font-semibold text-green-800 mb-2">Pengesahan QR:</p>
                    <a 
                      href={qrUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline break-all"
                    >
                      {qrUrl}
                    </a>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setCreatedMember(null);
                      setFormData({
                        school_id: '',
                        full_name: '',
                        ic_masked: '',
                        dob: '',
                        gender: 'Lelaki',
                        phone: '',
                        address: ''
                      });
                    }}
                    className="bg-[#D71920] hover:bg-[#b8161c]"
                  >
                    Daftar Ahli Lain
                  </Button>
                  <Button
                    onClick={() => navigate('/admin/members')}
                    variant="outline"
                  >
                    Lihat Senarai Ahli
                  </Button>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/members')}
            className="border-[#E5E5E5]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali
          </Button>
        </div>

        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 rounded-full bg-[#D71920]/10 flex items-center justify-center">
            <User className="w-8 h-8 text-[#D71920]" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" >
              Daftar Ahli Baru
            </h1>
            <p className="text-[#555]">Admin RSS mendaftarkan ahli untuk mana-mana perguruan</p>
          </div>
        </div>

        <div ></div>

        {schools.length === 0 && (
          <Alert className="border-yellow-300 bg-yellow-50">
            <AlertDescription className="text-yellow-800">
              Tiada perguruan Active dijumpai. Sila daftarkan atau luluskan perguruan terlebih dahulu.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-[#E5E5E5] p-8 space-y-6" data-testid="admin-member-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label htmlFor="school_id">Pilih Perguruan *</Label>
              <Select value={formData.school_id} onValueChange={(value) => setFormData({ ...formData, school_id: value })}>
                <SelectTrigger className="border-[#E5E5E5]" data-testid="select-school">
                  <SelectValue  />
                </SelectTrigger>
                <SelectContent>
                  {schools.map(school => (
                    <SelectItem key={school.id} value={school.id}>
                      {school.name} - {school.division}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="full_name">Nama Penuh *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                
                className="border-[#E5E5E5]"
                data-testid="input-name"
              />
            </div>

            <div>
              <Label htmlFor="ic_masked">Nombor IC (Tertutup)</Label>
              <Input
                id="ic_masked"
                value={formData.ic_masked}
                onChange={(e) => setFormData({ ...formData, ic_masked: e.target.value })}
                
                className="border-[#E5E5E5]"
                data-testid="input-ic"
              />
            </div>

            <div>
              <Label htmlFor="dob">Tarikh Lahir</Label>
              <Input
                id="dob"
                type="date"
                value={formData.dob}
                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                className="border-[#E5E5E5]"
                data-testid="input-dob"
              />
            </div>

            <div>
              <Label htmlFor="gender">Jantina</Label>
              <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                <SelectTrigger className="border-[#E5E5E5]" data-testid="input-gender">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Lelaki">Lelaki</SelectItem>
                  <SelectItem value="Perempuan">Perempuan</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                
                className="border-[#E5E5E5]"
                data-testid="input-phone"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="address">Alamat</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                
                className="border-[#E5E5E5]"
                data-testid="input-address"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-[#E5E5E5]">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/members')}
              className="border-[#E5E5E5]"
              data-testid="cancel-button"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={loading || schools.length === 0}
              className="bg-[#D71920] hover:bg-[#b8161c] text-white px-8"
              data-testid="submit-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Mendaftarkan...' : 'Daftar Ahli'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminMemberForm;
